import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { appConfig } from '@/api/appConfig';
import type { M3ApiResponse } from '@/types/api';
import {
  DistributionTypeEnum,
  OperationGroupEnum,
  InternalFeeOperationTypeEnum,
  InternalRevenueOperationTypeEnum,
  isInternalGroup,
  isExternalGroup,
} from '@/types/pricing';
import type {
  InternalOperationModel,
  ExternalOperationModel,
  ExternalDistributionModel,
  PricingPayloadModel,
  PricingApiPayload,
  GridModel,
} from '@/types/pricing';

const base = appConfig.adminApi;

// ─── Query keys ──────────────────────────────────────────────────────────────

export const PRICING_KEYS = {
  internalFeeOps: ['pricing', 'internal-fee-operations'] as const,
  internalRevenueOps: ['pricing', 'internal-revenue-operations'] as const,
  externalFeeOps: ['pricing', 'external-fee-operations'] as const,
  externalRevenueOps: ['pricing', 'external-revenue-operations'] as const,
  globalPricing: (group: OperationGroupEnum, id: number) =>
    ['pricing', 'global', group, id] as const,
  externalDistribution: (id: number) =>
    ['pricing', 'external-distribution', id] as const,
  businessEntities: ['pricing', 'business-entities'] as const,
  businessEntityDetails: (id: number) =>
    ['pricing', 'be-details', id] as const,
  businessEntityOperations: (id: number) =>
    ['pricing', 'be-operations', id] as const,
  businessEntityGrids: (
    beId: number,
    group: OperationGroupEnum,
    opType: string,
  ) => ['pricing', 'be-grids', beId, group, opType] as const,
} as const;

// ─── Payload builder (mirrors PricingService.setPayload) ─────────────────────

export function buildApiPayload(payload: PricingPayloadModel): PricingApiPayload {
  const skeleton: PricingApiPayload = {
    id: payload.id,
    distributionType: payload.distributionType,
    operationType: payload.operationType.toUpperCase(),
    grids: [],
    percentage: null,
    fixed: null,
    periodType: payload.periodType ?? null,
  };

  switch (payload.distributionType) {
    case DistributionTypeEnum.GRID:
    case DistributionTypeEnum.LIMITED_FREE_GRID:
      return { ...skeleton, grids: cleanGrids(payload.grids) };
    case DistributionTypeEnum.PERCENTAGE:
      return { ...skeleton, percentage: payload.percentage ?? null };
    case DistributionTypeEnum.FIXED:
      return { ...skeleton, fixed: payload.fixed ?? null };
    case DistributionTypeEnum.FIXED_PLUS_BUSINESS_ENTITY_WEIGHT_AVG:
      return {
        ...skeleton,
        fixed: payload.fixed ?? null,
        grids: cleanGrids(payload.grids),
      };
    case DistributionTypeEnum.PERCENTAGE_GRID:
      return {
        ...skeleton,
        percentage: payload.percentage ?? null,
        grids: cleanGrids(payload.grids),
      };
    default:
      return skeleton;
  }
}

function cleanGrids(grids?: GridModel[]): GridModel[] {
  return (grids ?? []).map((g) => {
    if (!g.id) {
      const { id: _id, ...rest } = g;
      void _id;
      return rest;
    }
    return g;
  });
}

// ─── Operations ──────────────────────────────────────────────────────────────

export function useInternalFeeOperationsQuery() {
  return useQuery({
    queryKey: PRICING_KEYS.internalFeeOps,
    queryFn: async () => {
      const { data } = await apiClient.get<
        M3ApiResponse<InternalOperationModel[]>
      >(`${base}/getInternalFeeOperations`);
      return data.result ?? [];
    },
  });
}

export function useInternalRevenueOperationsQuery() {
  return useQuery({
    queryKey: PRICING_KEYS.internalRevenueOps,
    queryFn: async () => {
      const { data } = await apiClient.get<
        M3ApiResponse<InternalOperationModel[]>
      >(`${base}/getInternalRevenueOperations`);
      return data.result ?? [];
    },
  });
}

export function useExternalFeeOperationsQuery() {
  return useQuery({
    queryKey: PRICING_KEYS.externalFeeOps,
    queryFn: async () => {
      const { data } = await apiClient.get<
        M3ApiResponse<ExternalOperationModel[]>
      >(`${base}/getExternalFeeOperations`);
      return data.result ?? [];
    },
  });
}

export function useExternalRevenueOperationsQuery() {
  return useQuery({
    queryKey: PRICING_KEYS.externalRevenueOps,
    queryFn: async () => {
      const { data } = await apiClient.get<
        M3ApiResponse<ExternalOperationModel[]>
      >(`${base}/getExternalRevenueOperations`);
      return data.result ?? [];
    },
  });
}

// ─── Global pricing detail/update ────────────────────────────────────────────

export function useGlobalPricingQuery(
  operationGroup: OperationGroupEnum,
  operationId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: PRICING_KEYS.globalPricing(operationGroup, operationId),
    queryFn: async () => {
      if (!isInternalGroup(operationGroup)) {
        throw new Error(
          `useGlobalPricingQuery only supports internal groups; got ${operationGroup}`,
        );
      }
      const urlSuffix =
        operationGroup === OperationGroupEnum.INTERNAL_FEE
          ? 'getInternalFeeDistribution'
          : 'getInternalRevenueDistribution';
      const { data } = await apiClient.get<M3ApiResponse<PricingPayloadModel>>(
        `${base}/${urlSuffix}/${operationId}`,
      );
      return data.result;
    },
    enabled: enabled && isInternalGroup(operationGroup) && operationId > 0,
  });
}

export function useExternalOperationDetailsQuery(
  nostroOperationId: number,
  enabled = true,
) {
  return useQuery({
    queryKey: PRICING_KEYS.externalDistribution(nostroOperationId),
    queryFn: async () => {
      const { data } = await apiClient.get<
        M3ApiResponse<ExternalDistributionModel>
      >(`${base}/getExternalDistribution/${nostroOperationId}`);
      return data.result;
    },
    enabled: enabled && nostroOperationId > 0,
  });
}

export function useUpdateGlobalPricingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      payload,
      operationGroup,
    }: {
      payload: PricingPayloadModel;
      operationGroup: OperationGroupEnum;
    }) => {
      const urlSuffix =
        operationGroup === OperationGroupEnum.INTERNAL_FEE
          ? 'updateInternalFeeDistribution'
          : 'updateInternalRevenueDistribution';
      const { data } = await apiClient.put<M3ApiResponse>(
        `${base}/${urlSuffix}`,
        buildApiPayload(payload),
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: PRICING_KEYS.globalPricing(
          vars.operationGroup,
          vars.payload.id ?? 0,
        ),
      });
    },
  });
}

export function useUpdateExternalOperationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inputData: ExternalDistributionModel) => {
      const { data } = await apiClient.put<M3ApiResponse>(
        `${base}/updateExternalDistribution`,
        inputData,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: PRICING_KEYS.externalDistribution(vars.nostroOperationId),
      });
    },
  });
}

// ─── Business entity pricing (consumed by phase3-pricing-be) ─────────────────

export function useBusinessEntitiesQuery() {
  return useQuery({
    queryKey: PRICING_KEYS.businessEntities,
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<unknown[]>>(
        `${base}/getBusinessEntitiesPricingList`,
      );
      return data.result ?? [];
    },
  });
}

export function useBusinessEntityDetailsQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: PRICING_KEYS.businessEntityDetails(id),
    queryFn: async () => {
      const { data } =
        await apiClient.get<M3ApiResponse<unknown>>(
          `${base}/getBusinessEntityById/${id}`,
        );
      return data.result;
    },
    enabled: enabled && id > 0,
  });
}

export function useBusinessEntityOperationsQuery(id: number, enabled = true) {
  return useQuery({
    queryKey: PRICING_KEYS.businessEntityOperations(id),
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<unknown[]>>(
        `${base}/getInternalOperationDistributionsByBusinessEntity/${id}`,
      );
      return data.result ?? [];
    },
    enabled: enabled && id > 0,
  });
}

export function useBusinessEntityGridsQuery(
  beId: number,
  operationGroup: OperationGroupEnum,
  operationType:
    | InternalFeeOperationTypeEnum
    | InternalRevenueOperationTypeEnum,
  enabled = true,
) {
  return useQuery({
    queryKey: PRICING_KEYS.businessEntityGrids(beId, operationGroup, operationType),
    queryFn: async () => {
      const urlSuffix =
        operationGroup === OperationGroupEnum.INTERNAL_FEE
          ? 'getInternalFeeDistributionByBusinessEntity'
          : 'getInternalRevenueDistributionByBusinessEntity';
      const { data } = await apiClient.post<M3ApiResponse<PricingPayloadModel>>(
        `${base}/${urlSuffix}`,
        { businessEntityId: beId, operationType: operationType.toUpperCase() },
      );
      return data.result;
    },
    enabled:
      enabled &&
      beId > 0 &&
      isInternalGroup(operationGroup),
  });
}

export function useUpdateBusinessEntityGridsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      beId,
      operationGroup,
      payload,
    }: {
      beId: number;
      operationGroup: OperationGroupEnum;
      payload: PricingPayloadModel;
    }) => {
      const urlSuffix =
        operationGroup === OperationGroupEnum.INTERNAL_FEE
          ? 'updateInternalFeeDistributionByBusinessEntity'
          : 'updateInternalRevenueDistributionByBusinessEntity';
      const { data } = await apiClient.post<M3ApiResponse>(
        `${base}/${urlSuffix}/${beId}`,
        buildApiPayload(payload),
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: PRICING_KEYS.businessEntityGrids(
          vars.beId,
          vars.operationGroup,
          vars.payload.operationType,
        ),
      });
    },
  });
}

export function useSetDefaultPricingForBEMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      businessEntityId,
      operationType,
      isRevenueOperation,
    }: {
      businessEntityId: number;
      operationType:
        | InternalFeeOperationTypeEnum
        | InternalRevenueOperationTypeEnum;
      isRevenueOperation: boolean;
    }) => {
      const endpoint = isRevenueOperation
        ? 'deleteInternalRevenueDistributionByBusinessEntity'
        : 'deleteInternalFeeDistributionByBusinessEntity';
      const { data } = await apiClient.delete<M3ApiResponse>(
        `${base}/${endpoint}`,
        { data: { businessEntityId, operationType } },
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: PRICING_KEYS.businessEntityOperations(vars.businessEntityId),
      });
    },
  });
}

// ─── Route-level guard helper (re-exported for update page) ──────────────────

export { isInternalGroup, isExternalGroup };
